import { useEffect, useRef, useState } from "react";
import type { KillSwitchStatus } from "@/api/types";

interface NotificationToastsProps {
  killSwitch: KillSwitchStatus | null;
}

type Toast = { id: string; tone: "bad"; title: string; message: string };

const TOAST_LIFETIME_MS = 8000;

const KILL_SWITCH_REASON_LABEL: Record<string, string> = {
  daily_loss: "daily loss limit",
  profit_target: "daily profit target",
  trading_disabled: "trading disabled",
  emergency_stop: "emergency stop",
  max_drawdown: "max drawdown",
  daily_trade_limit: "daily trade limit",
  outside_session_hours: "outside session hours",
  weekday_not_allowed: "weekday not allowed",
};

function toneClasses(tone: Toast["tone"]): string {
  return tone === "bad" ? "border-sell/50 bg-void text-sell" : "border-turquoise/50 bg-void text-turquoise";
}

export default function NotificationToasts({ killSwitch }: NotificationToastsProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const prevKillSwitchTriggered = useRef<boolean | null>(null);

  useEffect(() => {
    if (!killSwitch) return;
    const wasTriggered = prevKillSwitchTriggered.current;
    prevKillSwitchTriggered.current = killSwitch.triggered;
    if (wasTriggered === false && killSwitch.triggered) {
      const label = KILL_SWITCH_REASON_LABEL[killSwitch.reason ?? ""] ?? "unknown reason";
      setToasts((prev) => [
        ...prev,
        { id: crypto.randomUUID(), tone: "bad", title: "Kill switch triggered", message: `New entries halted: ${label}` },
      ]);
    }
  }, [killSwitch]);

  useEffect(() => {
    if (toasts.length === 0) return;
    const timers = toasts.map((t) =>
      setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== t.id)), TOAST_LIFETIME_MS),
    );
    return () => timers.forEach(clearTimeout);
  }, [toasts]);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-[60] flex flex-col gap-2 sm:left-auto sm:w-80">
      {toasts.map((t) => (
        <div key={t.id} className={`rounded-lg border p-3 font-mono text-xs shadow-panel ${toneClasses(t.tone)}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="font-semibold uppercase tracking-wide">{t.title}</div>
            <button
              onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
              className="text-ink-faint hover:text-ink"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
          <div className="mt-1 leading-snug text-ink-dim">{t.message}</div>
        </div>
      ))}
    </div>
  );
}
